// See @store/AppPhaseStore.ts for the store loading order.
import { create } from 'zustand';
import { GridData } from '@data/GridData';
import { getResourceStore } from './ResourceStore';
import { getEnvStore } from './EnvStore';
import type { GridResources } from '@visualizer/types';
import { NotificationType } from '@visualizer/types';
import useNotificationStore from '@gui/store/notification';
import { FileFetcherV2 } from 'Loader/FileFetcherv2';

export enum LoadingStatus {
    INITIALIZING = 'initializing',
    LOADING = 'loading',
    LOADED = 'loaded',
    VISUALIZER_READY = 'visualizer-ready',
    ERROR = 'error',
}

export type DownloadItemStatus = 'queued' | 'downloading' | 'cached' | 'completed' | 'error';

export type DownloadQueueItem = {
    id: string;
    label: string;
    status: DownloadItemStatus;
    loadedBytes: number;
    totalBytes: number | null;
    rateBytesPerSecond: number;
    remainingSeconds: number | null;
    error?: string;
    startedAt: number;
    updatedAt: number;
};

export type FreeScreenRecordingState = {
    active: boolean;
    startedAtMs: number;
    maxDurationSec: number;
};

type LoadingStore = {
    loadingStatus: LoadingStatus;
    setLoadingStatus: (status: LoadingStatus) => void;

    loadGrid: () => Promise<boolean>;
    grid: GridData | null;
    progressVersion: number;
    fileFetcher: FileFetcherV2;
    downloadQueue: Record<string, DownloadQueueItem>;
    activeLinesDownloadKey: string | null;
    isAllLayersLinesDownloadActive: boolean;
    setActiveLinesDownloadKey: (key: string | null) => void;
    setAllLayersLinesDownloadActive: (active: boolean) => void;
    freeScreenRecording: FreeScreenRecordingState | null;
    setFreeScreenRecording: (recording: FreeScreenRecordingState | null) => void;
};

export const useLoadingStore = create<LoadingStore>((set, get) => ({
    loadingStatus: LoadingStatus.INITIALIZING,
    setLoadingStatus: (status: LoadingStatus) => set({ loadingStatus: status }),

    loadGrid: async (): Promise<boolean> => {
        if (get().loadingStatus !== LoadingStatus.INITIALIZING) return false;

        const { gridResources } = getResourceStore();

        checkGeodata(gridResources);
        if (!gridResources) return false;

        const grid = new GridData(gridResources);
        set({ grid, loadingStatus: LoadingStatus.LOADING, downloadQueue: {} });

        try {
            const fileFetcher = get().fileFetcher;

            await grid.load(fileFetcher);
            console.warn('Finished loading grid');
            set({ loadingStatus: LoadingStatus.LOADED });
            return true;
        } catch (error) {
            console.error('Error loading grid:', error, getGridDebugInfo('loadGrid.catch'));
            set({ loadingStatus: LoadingStatus.ERROR });
            return false;
        }
    },
    grid: null,
    progressVersion: 0,
    fileFetcher: new FileFetcherV2(),
    downloadQueue: {},
    activeLinesDownloadKey: null,
    isAllLayersLinesDownloadActive: false,
    setActiveLinesDownloadKey: (key: string | null) => set({ activeLinesDownloadKey: key }),
    setAllLayersLinesDownloadActive: (active: boolean) => set({ isAllLayersLinesDownloadActive: active }),
    freeScreenRecording: null,
    setFreeScreenRecording: (recording: FreeScreenRecordingState | null) => set({ freeScreenRecording: recording }),
}));

type GridDebugInfo = {
    phase: string;
    timestamp: string;
    loadingStatus: LoadingStatus;
    hasGrid: boolean;
    gridId: string | null;
    authStatus: string;
    env: string;
    gridResourcesId: string | null;
    geodataCount: number | null;
    downloadQueueSize: number;
    url: string | null;
};

function getGridDebugInfo(phase: string): GridDebugInfo {
    const loadingState = useLoadingStore.getState();
    const { gridId, gridResources } = getResourceStore();
    const { env } = getEnvStore();
    const url = typeof window === 'undefined' ? null : window.location.href;

    return {
        phase,
        timestamp: new Date().toISOString(),
        loadingStatus: loadingState.loadingStatus,
        hasGrid: !!loadingState.grid,
        gridId: gridId ?? null,
        authStatus: 'authenticated',
        env,
        gridResourcesId: gridResources?.id ?? null,
        geodataCount: gridResources?.geodatas?.length ?? null,
        downloadQueueSize: Object.keys(loadingState.downloadQueue ?? {}).length,
        url,
    };
}

export const getGrid = (): GridData => {
    const grid = useLoadingStore.getState().grid;
    if (!grid) {
        const debugInfo = getGridDebugInfo('getGrid');
        console.error('Grid not loaded', debugInfo);
        throw new Error(`Grid not loaded: ${JSON.stringify(debugInfo)}`);
    }

    return grid;
};

export const setGrid = (grid: GridData) => useLoadingStore.setState({ grid });

export const loadGrid = async (): Promise<boolean> => {
    return useLoadingStore.getState().loadGrid();
};

const PROGRESS_NOTIFY_INTERVAL = 500;
let lastProgressNotify = 0;

export const notifyProgressUpdate = (force = false) => {
    const now = Date.now();
    if (!force && now - lastProgressNotify < PROGRESS_NOTIFY_INTERVAL) {
        return;
    }
    lastProgressNotify = now;
    useLoadingStore.setState((state) => ({
        progressVersion: state.progressVersion + 1,
    }));
};

export const getLoadingStatus = () => useLoadingStore.getState().loadingStatus;

export const setLoadingStatus = (status: LoadingStatus) => useLoadingStore.setState({ loadingStatus: status });

export const clearDownloadQueue = () => {
    useLoadingStore.setState({ downloadQueue: {} });
};

export const getDownloadQueue = (): DownloadQueueItem[] => {
    const queue = useLoadingStore.getState().downloadQueue;
    return Object.values(queue).sort((a, b) => a.startedAt - b.startedAt);
};

type DownloadItemPatch = {
    label?: string;
    status?: DownloadItemStatus;
    loadedBytes?: number;
    totalBytes?: number | null;
    rateBytesPerSecond?: number;
    remainingSeconds?: number | null;
    error?: string;
};

export const patchDownloadQueueItem = (id: string, patch: DownloadItemPatch) => {
    const now = Date.now();
    useLoadingStore.setState((state) => {
        const prev = state.downloadQueue[id];
        const next: DownloadQueueItem = {
            id,
            label: patch.label ?? prev?.label ?? id,
            status: patch.status ?? prev?.status ?? 'queued',
            loadedBytes: patch.loadedBytes ?? prev?.loadedBytes ?? 0,
            totalBytes: patch.totalBytes !== undefined ? patch.totalBytes : prev?.totalBytes !== undefined ? prev.totalBytes : null,
            rateBytesPerSecond: patch.rateBytesPerSecond ?? prev?.rateBytesPerSecond ?? 0,
            remainingSeconds:
                patch.remainingSeconds !== undefined
                    ? patch.remainingSeconds
                    : prev?.remainingSeconds !== undefined
                      ? prev.remainingSeconds
                      : null,
            error: patch.error ?? prev?.error,
            startedAt: prev?.startedAt ?? now,
            updatedAt: now,
        };

        return {
            downloadQueue: {
                ...state.downloadQueue,
                [id]: next,
            },
        };
    });
};

function checkGeodata(gridResources: GridResources | null) {
    if (!gridResources) {
        return;
    }
    if (gridResources.geodatas.length == 0) {
        useNotificationStore.getState().showNotification('Flag at least on Geodata in the Webapp', NotificationType.Error);
        setLoadingStatus(LoadingStatus.ERROR);
    }
}
